<template>
	<div class="guestflip-item-wrapper">
		<div class="badge badge-danger" v-if="survey.deletedAt">Archived</div>
		<div class="row guestflip-item mx-0">
			<div class="col-md-4 pl-3">
				<div class="profile align-items-center">
					<div class="left"></div>
					<div class="right">
						<p class="blue-text">
							<span v-if="survey.public">
								<a
									class="ml-1 blue-text text-decoration-none"
									target="_blank"
									:href="publicUrl"
								>
									<i class="fa fa-external-link-alt"></i>
									<strong>{{ survey.title }}</strong>
								</a>
							</span>
							<span v-else>
								<strong>{{ survey.title }}</strong>
							</span>
						</p>
						<p class="mt-md-0 mt-2">
							<a
								v-bind:href="'/surveys/' + survey.id + '/edit'"
								class="blue-link mr-2"
								v-if="!survey.deletedAt && hideActions"
							>
								<i class="far fa-edit"></i>{{ __('EDIT') }}
							</a>
							<a
								href="#"
								@click.prevent="initiatePreview()"
								class="blue-link mr-2"
								v-if="!survey.deletedAt"
							>
								<span> <i class="fas fa-eye"></i>{{ __('PREVIEW') }} </span>
							</a>
							<a
								href="#"
								@click.prevent="openTranslateModal"
								class="blue-link"
								v-if="!survey.deletedAt && hideActions"
							>
								<span> <i class="fas fa-globe"></i>{{ __('TRANSLATE') }} </span>
							</a>
						</p>
						<div class="star-rating">
							<div class="stars-active" v-if="survey.averageScore.length">
								<div
									class="stars"
									v-bind:style="{
										width: survey.averageScore.length
											? survey.averageScore[0].aggregate + '%'
											: '0',
									}"
								>
									<i class="fa fa-star" v-for="i in this.surveyOutOf"></i>
								</div>
							</div>
							<div class="stars-inactive" v-if="survey.averageScore.length">
								<i class="fa fa-star" v-for="i in this.surveyOutOf"></i>
							</div>
							<div class="rating-number" v-if="survey.averageScore.length">
								{{
									Math.round(survey.averageScore[0].aggregate) /
									(100 / surveyOutOf)
								}}/{{ surveyOutOf }}
							</div>
							<div class="rating-number" v-if="!survey.averageScore.length">
								{{ __('Score N/A') }}
							</div>
						</div>
					</div>
				</div>
				<table>
					<tbody>
						<tr>
							<th scope="row">{{ __('Status') }}</th>
							<td class="source" v-if="survey.deletedAt != null">
								{{ __('Archived') }}
							</td>
							<td class="source" v-else-if="survey.status === 'active'">
								{{ __('Live') }}
							</td>
							<td class="source" v-else-if="survey.status === 'draft'">
								{{ __('Disabled') }}
							</td>
						</tr>
						<tr>
							<th scope="row">{{ __('Date Created') }}</th>
							<td>{{ survey.createdAt | formatDate }}</td>
						</tr>
						<tr>
							<th scope="row">{{ __('Filled') }}</th>
							<td>{{ survey.timesFilled }} {{ __('times') }}</td>
						</tr>
					</tbody>
				</table>
			</div>
			<div class="col-md-8">
				<!-- Desktop view -->
				<div class="d-md-block d-none">
					<div class="metrics row">
						<div class="col-md-4 metric-wrapper border-right">
							<p class="metric-label">
								<strong>{{ __('Sent Today') }}</strong>
							</p>
							<p class="metric-number">
								{{ survey.dayStats ? survey.dayStats.totalSent : 0 }}
							</p>
						</div>
						<div class="col-md-4 metric-wrapper border-right">
							<p class="metric-label">
								<strong>{{ __('Sent Last Week') }}</strong>
							</p>
							<p class="metric-number">
								{{
									survey.weekStats && survey.weekStats.length
										? survey.weekStats[0].totalSent
										: 0
								}}
							</p>
						</div>
						<div class="col-md-4 metric-wrapper">
							<p class="metric-label">
								<strong>{{ __('Sent Total') }}</strong>
							</p>
							<p class="metric-number">
								{{
									survey.totalStats && survey.totalStats.length
										? survey.totalStats[0].totalSent
										: 0
								}}
							</p>
						</div>
					</div>
					<div class="link-actions">
						<a href="#" @click.prevent="initiateStatistics" class="blue-link">
							<span>
								<i class="fas fa-chart-pie"></i> {{ __('VIEW ALL STATS') }}
                   			</span>
						</a>
						<a
							v-if="survey.caseFiles.length > 0"
							href="#"
							@click.prevent="toggleCases"
							class="blue-link ml-4"
                    	>
							<span>
								<i class="fas fa-folder-open"></i>
								{{ __('VIEW CASES') }}
							</span>
						</a>
					</div>
				</div>
				<!-- Mobile view -->
				<div class="d-md-none d-block">
					<div class="d-flex justify-content-between my-3">
						<table class="metrics-table">
							<tbody>
								<tr>
									<th scope="row">{{ __('Sent Today') }}</th>
									<td>
										<strong>{{
											survey.dayStats ? survey.dayStats.totalSent : 0
										}}</strong>
									</td>
								</tr>
								<tr>
									<th scope="row">{{ __('Sent Last Week') }}</th>
									<td>
										<strong>{{
											survey.weekStats && survey.weekStats.length
												? survey.weekStats[0].totalSent
												: 0
										}}</strong>
									</td>
								</tr>
								<tr>
									<th scope="row">{{ __('Sent Total') }}</th>
									<td>
										<strong>{{
											survey.totalStats && survey.totalStats.length
												? survey.totalStats[0].totalSent
												: 0
										}}</strong>
									</td>
								</tr>
							</tbody>
						</table>
						<div class="link-actions">
							<a href="#" @click.prevent="initiateStatistics" class="blue-link">
								<span>
									<i class="fas fa-chart-pie"></i> {{ __('VIEW ALL STATS') }}
								</span>
							</a>
						</div>
					</div>
				</div>
			</div>
		</div>
		<!-- Desktop view -->
		<div class="d-md-block d-none">
			<item-buttons
				action-type="secondary"
				:secondary-actions="survey.deletedAt != null ? false : true"
			>
				<div slot="secondary-buttons" class="d-inline-block">
					<button class="action" @click="toggleSurveyStatus">
						<span
							:class="{
								'gradient-success': survey.status == 'draft',
								'gradient-danger': survey.status == 'active',
							}"
						>
							<i
								class="fas fa-play gradient-success"
								v-if="survey.status == 'draft'"
							></i>
							<i class="fas fa-pause gradient-danger" v-else></i>
							{{ survey.status == 'draft' ? __('Set Live') : __('Pause') }}
						</span>
					</button>
					<a href="#" @click.prevent="togglePublicStatus" class="action">
						<span v-if="survey.public == 0">
							{{ __('Make Public') }}
						</span>
						<span v-else>
							{{ __('Make Non-Public') }}
						</span>
						<i class="fas fa-globe"></i>
					</a>
					<a href="#" @click.prevent="archiveSurvey" class="action">
						<span class="gradient-danger">{{ __('Archive Data') }}</span>
						<i class="fas fa-download"></i>
					</a>
					<a href="#" @click.prevent="setTargets" class="action">
						<span>{{ __('Set Targets') }}</span>
						<i class="fas fa-bullseye"></i>
					</a>
				</div>
				<div class="d-inline-block align-middle" slot="primary-button">
					<a
						href="#"
						@click.prevent="initiateSending"
						class="btn btn-large btn-primary"
						v-if="opening && !survey.deletedAt"
					>
						<span v-if="opening">{{ __('Opening..') }}</span>
					</a>
					<a
						href="#"
						@click.prevent="initiateSending"
						class="btn btn-large btn-primary"
						v-else-if="!opening && !survey.deletedAt && survey.status === 'active'"
					>
						<span> {{ __('Send Now') }} <i class="fas fa-upload"></i> </span>
					</a>
				</div>
			</item-buttons>
		</div>
		<!-- Mobile view with dropdown appearing to the left -->
		<div class="d-md-none d-flex justify-content-between align-items-center px-2 mb-4">
			<!-- "Send Now" Button -->
			<div v-if="!survey.deletedAt && survey.status === 'active'" class="mr-auto">
				<button class="btn btn-large btn-primary" @click.prevent="initiateSending">
					{{ __('Send Now') }}
					<i class="fas fa-upload"></i>
				</button>
			</div>
			<!-- Dropdown Menu for Other Actions -->
			<div
				v-if="spark.userPermissions.manage_surveys"
				:class="{ 'ml-auto': !survey.deletedAt || survey.status !== 'active' }"
				class="dropdown"
			>
				<button
					class="btn btn-large btn-secondary dropdown-toggle"
					type="button"
					id="mobileActions"
					data-toggle="dropdown"
					aria-haspopup="true"
					aria-expanded="false"
				>
					{{ __('Actions') }}
				</button>
				<div class="dropdown-menu dropdown-menu-left" aria-labelledby="mobileActions">
					<a class="dropdown-item" href="#" @click="toggleSurveyStatus">
						<i class="fas fa-play" v-if="survey.status == 'draft'"></i>
						<i class="fas fa-pause" v-else></i>
						{{ survey.status == 'draft' ? __('Set Live') : __('Pause') }}
					</a>
					<a class="dropdown-item" href="#" @click.prevent="togglePublicStatus">
						<i class="fas fa-globe"></i>
						{{ survey.public === 0 ? __('Make Public') : __('Make Non-Public') }}
					</a>
					<a class="dropdown-item text-danger" href="#" @click.prevent="archiveSurvey">
						<i class="fas fa-download"></i> {{ __('Archive Data') }}
					</a>
					<a class="dropdown-item" href="#" @click.prevent="setTargets">
						<i class="fas fa-bullseye"></i> {{ __('Set Targets') }}
					</a>
				</div>
			</div>
		</div>

		<div v-if="sendingMode">
			<div class="modal" ref="sending_modal" tabindex="-1" role="dialog">
				<div class="modal-dialog modal-lg modal-dialog-centered" role="document">
					<div class="modal-content">
						<div class="modal-header">
							<h5 class="modal-title">Send Survey</h5>
							<button
								type="button"
								class="close"
								data-dismiss="modal"
								aria-label="Close"
								v-on:click="clearEmails"
							>
								<span aria-hidden="true">&times;</span>
							</button>
						</div>

						<div class="modal-body">
							<div class="mb-4">
								<p class="text-center">
									Please enter one email per line. The emails will be sent shortly
									after you upload them to our platform.
								</p>
							</div>

							<div class="alert alert-danger" v-if="invalidEmails.length">
								<p>
									The following email addresses are invalid. Please fix them or
									remove them from the list and try again:
								</p>
								<ul>
									<li v-for="email in invalidEmails" :key="email">
										@{{ email }}
									</li>
								</ul>
							</div>

							<ul
								class="nav nav-pills mb-3 justify-content-center"
								id="send-actions-tabs"
								role="tablist"
							>
								<li class="nav-item">
									<a
										class="nav-link active"
										id="send-csv-tab"
										data-toggle="pill"
										href="#send-csv"
										role="tab"
										aria-controls="send-csv"
										aria-selected="true"
									>
										<i class="far fa-file-alt"></i> Send from CSV File
									</a>
								</li>
								<li class="nav-item">
									<a
										class="nav-link"
										id="send-textarea-tab"
										data-toggle="pill"
										href="#send-textarea"
										role="tab"
										aria-controls="send-textarea"
										aria-selected="false"
									>
										<i class="far fa-keyboard"></i> Manually Type Emails
									</a>
								</li>
								<li class="nav-item" v-if="survey.public == 1">
									<a
										class="nav-link"
										id="send-public-tab"
										data-toggle="pill"
										href="#send-public"
										role="tab"
										aria-controls="send-public"
										aria-selected="false"
									>
										<i class="fa fa-globe"></i> Public Sharing
									</a>
								</li>
							</ul>

							<div class="tab-content" id="pills-tabContent">
								<div
									class="tab-pane fade show active"
									id="send-csv"
									role="tabpanel"
									aria-labelledby="send-csv-tab"
								>
									<div
										class="d-flex flex-wrap align-items-center justify-content-between mb-3"
									>
										<a
											class="btn btn-primary"
											data-toggle="collapse"
											href="#csvCols"
											role="button"
											aria-expanded="false"
											aria-controls="csvCols"
										>
											View CSV Columns
										</a>
										<a
											class="text-primary"
											target="_blank"
											href="/downloads/sample.csv"
											>Download Sample CSV</a
										>
									</div>
									<div class="collapse" id="csvCols">
										<table class="table table-bordered table-responsive">
											<tbody>
												<tr>
													<th>first_name</th>
													<td>The first name of the guest.</td>
												</tr>
												<tr>
													<th>last_name</th>
													<td>The last name of the guest.</td>
												</tr>
												<tr>
													<th>email</th>
													<td>The email to send the survey to.</td>
												</tr>
												<tr>
													<th>language_code</th>
													<td>
														The preferred language of the guest in
														<a
															class="text-primary"
															target="_blank"
															href="https://en.wikipedia.org/wiki/ISO_639-3"
															>ISO 639-3</a
														>
														format.
													</td>
												</tr>
												<tr>
													<th>room_id</th>
													<td>
														The room number or unique code where the
														guest is staying in. For alternative forms
														of accommodation this can be the bed.
													</td>
												</tr>
												<tr>
													<th>check_in_date</th>
													<td>
														The date when the guest checked in. Expected
														format: 2020-01-01 00:00:00
													</td>
												</tr>
												<tr>
													<th>check_out_date</th>
													<td>
														The date when the guest is expected to check
														out. Expected format: 2020-01-01 00:00:00
													</td>
												</tr>
											</tbody>
										</table>
									</div>
									<vue-dropzone
										v-on:vdropzone-complete="listUploaded"
										ref="myVueDropzone"
										id="dropzone"
										:options="dropzoneOptions"
									></vue-dropzone>
									<div class="mt-3" v-if="sendToGuests.length > 0">
										<div
											class="mb-3 d-flex flex-wrap align-items-center justify-content-between"
										>
											<a
												class="btn btn-primary"
												data-toggle="collapse"
												href="#previewGuests"
												role="button"
												aria-expanded="false"
												aria-controls="previewGuests"
											>
												Preview Sending
											</a>
											<strong class="text-warning"
												>Please check if dates appear correctly.</strong
											>
										</div>
										<div class="collapse" id="previewGuests">
											<div class="alert alert-warning">
												Dates are expected in Year-Month-Date format (e.g.,
												2020-01-01). Please confirm they are correct. If
												not, try importing them in the correct format or
												contact support.
											</div>
											<table class="table table-bordered table-responsive">
												<thead>
													<tr>
														<th>First Name</th>
														<th>Last Name</th>
														<th>Language Code</th>
														<th>Email</th>
														<th>Check-in Date</th>
														<th>Check-out Date</th>
														<th>Room #</th>
													</tr>
												</thead>
												<tbody>
													<tr
														v-for="guest in sendToGuests"
														:key="guest.email"
													>
														<td>{{ guest.firstName }}</td>
														<td>{{ guest.lastName }}</td>
														<td>{{ guest.languageCode }}</td>
														<td>{{ guest.email }}</td>
														<td>{{ guest.checkInDate }}</td>
														<td>{{ guest.checkOutDate }}</td>
														<td>{{ guest.roomId }}</td>
													</tr>
												</tbody>
											</table>
										</div>
									</div>
									<button
										class="btn btn-primary mt-3"
										v-bind:disabled="uploadingMode"
										v-on:click="uploadEmailsToSurvey"
									>
										Upload
									</button>
								</div>

								<div
									class="tab-pane fade"
									id="send-textarea"
									role="tabpanel"
									aria-labelledby="send-textarea-tab"
								>
									<textarea
										v-model="sendToEmails"
										class="form-control"
										style="min-height: 200px"
									></textarea>
									<button
										class="btn btn-primary mt-3"
										v-bind:disabled="uploadingMode"
										v-on:click="uploadEmailsToSurvey"
									>
										Upload
									</button>
								</div>

								<div
									class="tab-pane fade"
									id="send-public"
									v-if="survey.public"
									role="tabpanel"
									aria-labelledby="send-public-tab"
								>
									<div class="mb-4">
										<p><strong>Copy Public URL</strong></p>
										<input
											type="text"
											class="form-control"
											readonly
											:value="surveysUrl + '/' + survey.slug"
										/>
									</div>
									<div>
										<p><strong>Get QR Code</strong></p>
										<img :src="qrSource" />
									</div>
								</div>
							</div>
						</div>
					</div>
				</div>
			</div>
		</div>

		<div v-if="settingTargets">
			<div class="modal" ref="targets_modal" tabindex="-1" role="dialog">
				<div class="modal-dialog modal-lg modal-dialog-centered" role="document">
					<div class="modal-content">
						<div class="modal-header">
							<h5 class="modal-title">Set targets</h5>
							<button
								type="button"
								class="close"
								data-dismiss="modal"
								aria-label="Close"
								v-on:click="clearEmails"
							>
								<span aria-hidden="true">&times;</span>
							</button>
						</div>
						<div class="modal-body">
							<survey-set-targets :survey="survey" />
						</div>
					</div>
				</div>
			</div>
		</div>

		<div>
			<div
				class="modal gf-modal survey-preview-modal"
				ref="translate_modal"
				tabindex="-1"
				role="dialog"
			>
				<div class="modal-dialog modal-lg modal-dialog-centered" role="document">
					<div class="modal-content">
						<div class="modal-header">
							<h5 class="modal-title">Translate your survey</h5>
							<button
								type="button"
								class="close"
								data-dismiss="modal"
								aria-label="Close"
							>
								<span aria-hidden="true">&times;</span>
							</button>
						</div>
						<div class="modal-body">
							<survey-translation :survey="survey"> </survey-translation>
						</div>
						<div class="modal-footer"></div>
					</div>
				</div>
			</div>
		</div>

		<div v-if="previewMode">
			<div
				class="modal gf-modal survey-preview-modal"
				ref="preview_modal"
				tabindex="-1"
				role="dialog"
			>
				<div class="modal-dialog full-width" role="document">
					<div class="modal-content">
						<div class="modal-header">
							<h5 class="modal-title">Preview your survey</h5>
							<button
								type="button"
								class="close"
								data-dismiss="modal"
								aria-label="Close"
							>
								<span aria-hidden="true">&times;</span>
							</button>
						</div>
						<div class="modal-body" style="background: white">
							<div class="row">
								<div class="col-md-4 mb-4 text-center">
									<div class="btn-group toggle-responsive-container">
										<div
											class="btn cursor-pointer"
											v-bind:class="{ 'btn-primary': !responsiveEnabled }"
											v-on:click="responsiveEnabled = false"
										>
											<i class="fas fa-desktop"></i>
										</div>
										<div
											class="btn cursor-pointer"
											v-bind:class="{ 'btn-primary': responsiveEnabled }"
											v-on:click="responsiveEnabled = true"
										>
											<i class="fas fa-mobile-alt"></i>
										</div>
									</div>
								</div>
								<div class="col-md-4 mb-4 text-center">
									<div class="btn-group">
										<div
											class="btn cursor-pointer"
											v-bind:class="{ 'btn-primary': state === 'email' }"
											v-on:click="state = 'email'"
										>
											Guest Email
										</div>
										<div
											class="btn cursor-pointer"
											v-bind:class="{ 'btn-primary': state === 'survey' }"
											v-on:click="state = 'survey'"
										>
											Survey Page
										</div>
										<div
											class="btn cursor-pointer"
											v-bind:class="{
												'btn-primary': state === 'thankYouMessage',
											}"
											v-on:click="state = 'thankYouMessage'"
										>
											Thank You Page
										</div>
									</div>
								</div>
								<div class="col-md-4 mb-4 text-center">
									<div
										class="btn cursor-pointer toggle-live-btn"
										:class="{
											'btn-success btn-gradient-success':
												survey.status == 'draft',
											'btn-danger btn-danger-gradient':
												survey.status == 'active',
										}"
										v-on:click="toggleSurveyStatus"
									>
										<i
											:class="{
												'fas fa-play-circle': survey.status == 'draft',
												'fas fa-pause': survey.status == 'active',
											}"
										></i
										>{{ survey.status == 'draft' ? 'Set Live' : 'Pause' }}
									</div>
								</div>
							</div>
							<div
								class="col-md-12 mt-4"
								v-if="state === 'survey'"
								v-bind:style="getContainerStyle()"
								style="
									height: 600px;
									background-repeat: no-repeat;
									background-position: center center;
								"
							>
								<iframe
									v-bind:style="getIframeStyle()"
									style="border: 0; background: white"
									:src="surveysUrl + '/' + survey.slug + '/' + token"
								></iframe>
							</div>
							<div
								class="col-md-12 mt-4"
								v-if="state === 'thankYouMessage'"
								v-bind:style="getContainerStyle()"
								style="
									height: 600px;
									background-repeat: no-repeat;
									background-position: center center;
								"
							>
								<iframe
									v-bind:style="getIframeStyle()"
									style="border: 0; background: white"
									:src="surveysUrl + '/' + survey.slug + '/' + thankYouToken"
								></iframe>
							</div>
							<div
								class="col-md-12 mt-4"
								v-if="state === 'email'"
								v-bind:style="getContainerStyle()"
								style="
									height: 600px;
									background-repeat: no-repeat;
									background-position: center center;
								"
							>
								<iframe
									v-bind:style="getIframeStyle()"
									style="border: 0; background: white"
									:src="
										apiUrl +
										'/private/email/preview/sendSurvey?surveyId=' +
										survey.id +
										'&responsive=' +
										responsiveEnabled +
										'&jwt=' +
										jwt
									"
								></iframe>
							</div>
						</div>
						<div class="modal-footer"></div>
					</div>
				</div>
			</div>
		</div>
	    <div v-if="caseDetailsVisible">
        <div class="modal" ref="case_modal" tabindex="-1" role="dialog">
            <div class="modal-dialog" role="document">
                <div class="modal-content">
                    <div class="modal-header">
                        <h5 class="modal-title">{{ `${survey.title}'s ${__('cases')}` }}</h5>
                        <button type="button" class="close" @click="toggleCases" aria-label="Close">
                            <span aria-hidden="true">&times;</span>
                        </button>
                    </div>
                    <div class="modal-body">
                        <div class="survey-cases cases-section">
                            <div class="row">
                                <div
                                    v-for="caseFile in survey.caseFiles"
                                    :key="caseFile.caseFileId"
                                    class="col-md-6 col-12 mb-2">
                                    <div
                                        :class="[
                                            'case-wrapper',
                                            caseFilepriorityStyle(caseFile.priority)
                                        ]">
                                        <h5 class="header-case blue mb-0">
                                            <a
                                                href="#"
                                                @click.prevent="viewCase(caseFile.id)"
                                                class="text-primary">
                                                {{ caseFile.title }}
                                            </a>
                                        </h5>
                                        <div class="badges-container">
                                            <span class="badge badge-primary">{{ caseFile.type }}</span>
                                            <span
                                                :class="[
                                                    'badge',
                                                    caseFileStatusBadge(caseFile.status)
                                                ]">
                                                {{ caseFile.status }}
                                            </span>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    </div>
</div>
</template>

<script>
import SurveysService from '../../services/surveys-service';
import PieChart from '../charts/pie-chart';
import HorizontalBarChart from '../charts/horizontal-bar-chart';
import BarChart from '../charts/bar-chart';
import vue2Dropzone from 'vue2-dropzone';
import 'vue2-dropzone/dist/vue2Dropzone.min.css';
import SurveySetTargets from './SurveySetTargets.vue';
import CaseService from '../../services/cases-service';
// import ChartJsPluginDataLabels from 'chartjs-plugin-datalabels';

export default {
	props: ['survey', 'hasActiveCases'],
	components: {
		HorizontalBarChart,
		PieChart,
		BarChart,
		vueDropzone: vue2Dropzone,
		SurveySetTargets,
	},
	data() {
		return {
            caseDetailsVisible: false,
			stats: {},
			languages: [],
			translations: null,
			dropzoneOptions: {
				url: process.env.MIX_API_URL + '/private/upload/file/list',
				acceptedFiles: '.csv',
				addRemoveLinks: true,
				maxFilesize: 2,
				maxFiles: 1,
				headers: { Authorization: Spark.jwtToken },
			},
			translationLanguageId: null,
			statisticsMode: false,
			jwt: null,
			apiUrl: null,
			surveysUrl: null,
			sendingMode: false,
			settingTargets: false,
			previewMode: false,
			uploadingMode: false,
			responsiveEnabled: false,
			translationStep: 'selectLanguage',
			state: 'email',
			token: null,
			thankYouToken: null,
			invalidEmails: [],
			sendToEmails: '',
			sendToGuests: [],
			opening: false,
			textFieldsInfo: {},
		};
	},
	async mounted() {
		this.fetchCaseFileDetails();
		this.jwt = Spark.jwtToken;
		this.apiUrl = process.env.MIX_API_URL;
		this.surveysUrl = process.env.MIX_SURVEYS_URL;
	},
	computed: {
		hideActions() {
			return Spark.userPermissions && Spark.userPermissions.manage_surveys === 1;
		},
		publicUrl() {
			return this.surveysUrl + '/' + this.survey.slug;
		},
		qrSource() {
			return `https://quickchart.io/qr?text=${this.publicUrl}&ecLevel=H&size=300&format=svg`;
		},
		surveyOutOf() {
			if (!this.survey) {
				return 10;
			}
			return this.survey.options.outOfFive === 1 ? 5 : 10;
		},
	},
	methods: {
		async fetchCaseFileDetails() {
			if (this.survey.caseFiles && this.survey.caseFiles.length > 0) {
				try {
					const caseFilePromises = this.survey.caseFiles.map(async (caseFile) => {
						return await CaseService.getCaseDetails(Spark.state.currentTeam.id, caseFile.caseFileId);
					});

					this.survey.caseFiles = await Promise.all(caseFilePromises);
				} catch (error) {
					console.error("Error fetching case details:", error);
				}
			}
		},
        viewCase(caseId) {
            const url = `/cases?caseId=${caseId}#cases-view`;
            window.location.href = url;
        },
        caseFileStatusBadge(status) {
            switch (status.toLowerCase()) {
                case 'open':
                    return 'badge-success';
                case 'closed':
                    return 'badge-danger';
                case 'pending':
                    return 'badge-warning';
                default:
                    return 'badge-secondary'; 
            }
        },
        caseFilepriorityStyle(priority) {
            const priorities = {
                0: 'low-priority',
                1: 'medium-priority',
                2: 'high-priority',
            };

            return priorities[priority] || '';
        },
        toggleCases() {
            this.caseDetailsVisible = true
            this.$nextTick(function () {
                $(this.$refs.case_modal).modal('toggle')
                $(this.$refs.case_modal).on('hide.bs.modal', function (e) {
                    this.caseDetailsVisible = false
                });
            })
        },
        createCase() {
            const url = `/cases?surveyId=${this.survey.id}#case-create`;
            window.location.replace(url);
        },
		listUploaded(event) {
			let response = JSON.parse(event.xhr.response);
			this.sendToEmails = response.mails.join('\n');
			this.sendToGuests = response.guests;
		},
		toggleFieldBreakdown(idx) {
			if (this.stats.questionSummaries[idx].showBreakdown === undefined) {
				Vue.set(this.stats.questionSummaries[idx], 'showBreakdown', true);
				return true;
			}
			this.stats.questionSummaries[idx].showBreakdown =
				!this.stats.questionSummaries[idx].showBreakdown;
		},
		setTargets() {
			this.settingTargets = true;
			this.$nextTick(function () {
				$(this.$refs.targets_modal).modal('toggle');
				const vm = this;
				$(this.$refs.targets_modal).on('hide.bs.modal', function (e) {
					vm.sendingMode = false;
				});
			});
		},
		changeFieldCurrentIndex(idx, type) {
			if (type === 'dec') {
				if (this.textFieldsInfo[idx].currentIndex === 0) return;
				this.textFieldsInfo[idx].currentIndex--;
			} else if (type === 'inc') {
				if (
					this.textFieldsInfo[idx].currentIndex ===
					this.textFieldsInfo[idx].data.length - 1
				)
					return;
				this.textFieldsInfo[idx].currentIndex++;
			}
		},
		getDiagramData(item) {
			const data = {
				labels: item.results.map((x) => x.value),
				datasets: [
					{
						// label: 'Data',
						backgroundColor: '#f87979',
						data: item.results.map((x) => (x.total / item.totalResponses) * 100),
					},
				],
			};

			const options = {
				plugins: {
					datalabels: false,
				},
				scales: {
					xAxes: [
						{
							gridLines: {
								display: false,
							},
							ticks: {
								min: 0,
								max: 100,
								stepSize: 20, // minimum will be 0, unless there is a lower value.
								// OR //
								beginAtZero: true,
								callback: function (label, index, labels) {
									return label + '%';
								}, // minimum value will be 0.
							},
						},
					],
					yAxes: [
						{
							gridLines: {
								display: false,
							},
						},
					],
				},
				legend: {
					display: false,
				},
			};
			return { data, options };
		},
		getContainerStyle() {
			const backgroundImage = this.responsiveEnabled
				? '/img/surveys/phone.png'
				: '/img/surveys/macbook.png';
			const backgroundSize = this.responsiveEnabled ? '400px 605px' : '1200px 600px';
			return {
				'background-image': `url(${backgroundImage})`,
				'background-size': backgroundSize,
			};
		},
		getIframeStyle() {
			const width = this.responsiveEnabled ? '368px' : '925px';
			const height = this.responsiveEnabled ? '540px' : '496px';
			const marginLeft = this.responsiveEnabled ? '-183px' : '-464px';
			const marginTop = this.responsiveEnabled ? '32px' : '40px';
			const position = this.responsiveEnabled ? 'absolute' : 'absolute';
			const left = this.responsiveEnabled ? '50%' : '50%';
			return {
				width: width,
				height: height,
				'margin-left': marginLeft,
				'margin-top': marginTop,
				position: position,
				left: left,
			};
		},
		async toggleSurveyStatus() {
			let newStatus = this.survey.status == 'draft' ? 'active' : 'draft';
			const data = await SurveysService.setStatus(this.survey.id, newStatus);

			if (data.error) {
				swal({
					title: 'Something went wrong',
					text: data.error,
					type: 'warning',
					showCloseButton: true,
					timer: 10000,
				});
			} else {
				swal({
					title: 'Survey ' + (newStatus === 'draft' ? 'Disabled' : 'Enabled'),
					text: data.message,
					type: 'success',
					showCloseButton: true,
					timer: 10000,
				});
			}

			this.previewMode = false;
			$('.modal').modal('hide');

			this.$parent.getSurveys();
		},
		async togglePublicStatus() {
			let newStatus = this.survey.public == 1 ? 0 : 1;
			const data = await SurveysService.setPublicStatus(this.survey.id, newStatus);

			if (data.error) {
				swal({
					title: 'Something went wrong',
					text: data.error,
					type: 'warning',
					showCloseButton: true,
					timer: 10000,
				});
			} else {
				swal({
					title: 'Public access ' + (newStatus === 0 ? 'Disabled' : 'Enabled'),
					text: data.message,
					type: 'success',
					showCloseButton: true,
					timer: 10000,
				});
			}

			this.$parent.getSurveys();
		},
		async archiveSurvey() {
			swal(
				{
					title: 'Are you sure?',
					text: 'You will not be able to use the survey after the archive operation. All stats will remain available.',
					type: 'warning',
					showCancelButton: true,
					confirmButtonClass: 'btn-danger',
					confirmButtonText: 'Yes, archive it!',
					closeOnConfirm: false,
				},
				async () => {
					const data = await SurveysService.archive(this.survey.id);

					if (data.error) {
						swal({
							title: 'Archive not allowed',
							text: data.error,
							type: 'warning',
							showCloseButton: true,
							timer: 10000,
						});
					} else {
						swal({
							title: 'Survey Archived',
							text: data.message,
							type: 'success',
							showCloseButton: true,
							timer: 10000,
						});
						this.$parent.getSurveys();
					}
				},
			);
		},
		clearEmails() {
			this.sendToEmails = '';
		},
		initiateSending() {
			this.sendingMode = true;
			this.opening = true;
			this.$nextTick(function () {
				$(this.$refs.sending_modal).modal('toggle');
				this.opening = false;
				const vm = this;
				$(this.$refs.sending_modal).on('hide.bs.modal', function (e) {
					vm.sendingMode = false;
				});
			});
		},
		async initiateStatistics() {
			this.$eventHub.$emit('selectStatsSurvey', this.survey);
			$('.spark-settings-tabs a[href$="#survey-stats"]').tab('show');
		},
		async initiatePreview() {
			this.previewMode = true;
			this.opening = true;

			const data = await SurveysService.getPreviewToken(this.survey.id);
			this.token = data.token;
			this.thankYouToken = data.thankYouToken;

			this.$nextTick(function () {
				$(this.$refs.preview_modal).modal('toggle');
				this.opening = false;
			});
		},
		async openTranslateModal() {
			this.$emit('resetTranslationState');
			this.$nextTick(function () {
				$(this.$refs.translate_modal).modal('toggle');
			});
		},
		async uploadEmailsToSurvey() {
			if (this.uploadingMode) return;
			this.uploadingMode = true;

			this.invalidEmails = [];

			let processedEmails = this.sendToEmails
				.split('\n')
				.map((email) => email.trim())
				.filter((email) => email !== '');

			processedEmails.forEach((email) => {
				if (
					!email.match(
						/[a-z0-9!#$%&'*+/=?^_`{|}~-]+(?:\.[a-z0-9!#$%&'*+/=?^_`{|}~-]+)*@(?:[a-z0-9](?:[a-z0-9-]*[a-z0-9])?\.)+[a-z0-9](?:[a-z0-9-]*[a-z0-9])?/,
					)
				) {
					this.invalidEmails.push(email);
				}
			});

			if (this.invalidEmails.length) {
				this.uploadingMode = false;
				return false;
			}

			if (this.sendToGuests.length == 0) {
				this.sendToGuests = processedEmails.map((email) => {
					return {
						email,
					};
				});
			}

			const data = await SurveysService.uploadAdhocEmails(this.survey.id, this.sendToGuests);

			$(this.$refs.sending_modal).modal('toggle');

			this.uploadingMode = false;

			if (data.error) {
				var errorMessage = '';
				if (data.error.guests) {
					errorMessage = 'No emails have been provided.';
				} else {
					for (let key of Object.keys(data.error)) {
						const parts = key.split('.');
						errorMessage += `\nThere is a problem with ${parts[0]} #${parts[1]}, specifically their ${parts[2]}.`;
					}
				}

				swal({
					title: 'Upload failed',
					text: errorMessage,
					type: 'warning',
					showCloseButton: true,
					timer: 10000,
				});
			} else {
				this.sendToGuests = [];
				this.sendToEmails = '';
				swal({
					title: 'Upload completed',
					text: data.message,
					type: 'success',
					showCloseButton: true,
					timer: 10000,
				});
			}
		},
	},
};
</script>

<style scoped>
.card-header {
	justify-content: center !important;
}
</style>
